import { v4 as uuidV4 } from 'uuid';
import { now } from '@internationalized/date';
import { SuggestedSettingsEnum, SuggestedSettingsType } from '@lp/features/RoutePlanner/types';
import { Case, OrganType } from '@/api/apis/case';
import {
  GenerateMultiOrPlanBody,
  GeneratePartialPlanBody,
  RouteTypeSettingsEnum,
  StartSurgeonTripsGenerationBody,
} from '@/api/apis/mission-planner';
import { getUserTimezone, getUTCStringWithoutMs, getZonedUTCStringWithoutMs } from '@/utils/helpers';
import { getCaseOrTime } from './case-or-time';

type OrTimesDataType = {
  id: string;
  orTimeUTC: string;
  orTimeDiffMs: number;
};

type MultiOrSettingsBody = GenerateMultiOrPlanBody &
  Partial<StartSurgeonTripsGenerationBody> & { orTimesData: OrTimesDataType[] };

export const generateFullRouteRequestBody = (
  settingsList: SuggestedSettingsType[],
  caseData: Case,
): MultiOrSettingsBody => {
  const currentCaseOrTime = getCaseOrTime(
    caseData.donorHospitalDate,
    caseData.donorHospitalTime,
    caseData.donorHospitalTimeZone,
  );

  const baseRequestBody: Partial<GenerateMultiOrPlanBody> = {
    hospitalId: caseData.donorHospitalDetails!.id,
    txCenterId: caseData.txCenterDetails?.id,
    caseType: caseData.donorType,
    organType: caseData.organType,
    caseId: caseData.caseId,
    isTrialProgram: !!caseData.trialProgram,
    ...(caseData.trialProgram
      ? {
          trialProgramId: caseData.trialProgram.id,
        }
      : {}),
    doRunOptimizations: true,
    allowStandbyAtHosToOptimizeFlights: caseData.organType === OrganType.Liver,
  };

  return settingsList.reduce((acc, item) => {
    switch (item.type) {
      case SuggestedSettingsEnum.RouteType:
        acc.partialTripType = item.value;
        break;
      case SuggestedSettingsEnum.CS:
        if (item.value) {
          acc.forcedSpecialistId = item.value.employeeId;
        }
        break;
      case SuggestedSettingsEnum.RS:
        if (item.value) {
          acc.forcedSurgeonId = item.value.employeeId;
        }
        break;
      case SuggestedSettingsEnum.DeploymentHub:
        if (item.value) {
          acc.forcedHubId = item.value;
        }
        break;
      case SuggestedSettingsEnum.OrganTransportationType:
        acc.travelTypeHosTx = item.value;
        break;
      case SuggestedSettingsEnum.OR: {
        const offsets = Array.from({ length: item.value.offset + 1 }, (_, i) => i);
        const currentOrTimeUTC = currentCaseOrTime.toAbsoluteString();
        const currentOrTimeMs = new Date(currentOrTimeUTC).getTime();
        const orTimesData = offsets.map((offset) => {
          const orTimeUTC = item.value.selectedOr.add({ hours: offset }).toAbsoluteString();
          const orTimeMs = new Date(orTimeUTC).getTime();

          return {
            id: uuidV4(),
            orTimeUTC,
            orTimeDiffMs: orTimeMs - currentOrTimeMs,
          };
        });

        acc.orTimesData = orTimesData;
        acc.orTimes = orTimesData.map((orTime) => getUTCStringWithoutMs(orTime.orTimeUTC));

        break;
      }
      default:
        break;
    }
    return acc;
  }, baseRequestBody as MultiOrSettingsBody);
};

export const generatePartialRouteRequestBody = (
  settingsList: SuggestedSettingsType[],
  caseData: Case,
): GeneratePartialPlanBody => {
  const currentCaseOrTimeZoned = getCaseOrTime(
    caseData.donorHospitalDate,
    caseData.donorHospitalTime,
    caseData.donorHospitalTimeZone,
  );

  const routeType = settingsList.find((s) => s.type === SuggestedSettingsEnum.RouteType)?.value;
  const hasDonorHospitalSegment = routeType !== RouteTypeSettingsEnum.Custom_TXC_Hub;

  const baseRequestBody: Partial<GeneratePartialPlanBody> = {
    txCenterId: caseData.txCenterDetails!.id,
    organType: caseData.organType,
    caseId: caseData.caseId,
    currentTime: getZonedUTCStringWithoutMs(now(getUserTimezone())),
    allowStandbyAtHosToOptimizeFlights: caseData.organType === OrganType.Liver,
    ...(hasDonorHospitalSegment
      ? {
          hospitalId: caseData.donorHospitalDetails!.id,
          orTime: getZonedUTCStringWithoutMs(currentCaseOrTimeZoned),
        }
      : {}),
  };

  return settingsList.reduce((acc, item) => {
    switch (item.type) {
      case SuggestedSettingsEnum.RouteType:
        acc.partialTripType = item.value;
        break;
      case SuggestedSettingsEnum.ArrivalHub:
        if (item.value) {
          acc.hubId = item.value;
        }
        break;
      case SuggestedSettingsEnum.OrganTransportationType:
        acc.travelTypeHosTx = item.value;
        break;
      case SuggestedSettingsEnum.StartTime:
        acc.tripStartTime = getZonedUTCStringWithoutMs(item.value);
        break;
      case SuggestedSettingsEnum.StartLocation:
        if (item.value) {
          acc.tripStartLocation = {
            objectType: item.value.type,
            objectId: item.value.id,
            latitude: item.value.location.latitude,
            longitude: item.value.location.longitude,
          };
        }
        break;
      default:
        break;
    }

    return acc;
  }, baseRequestBody as GeneratePartialPlanBody);
};
